import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MoreHorizontal, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SettingsConfirmDialog from '@/components/settings/SettingsConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const EMPTY_MEMBER = { location_id: '', department_id: '' };

function emptyForm() {
  return {
    id: null,
    name: '',
    min_headcount: '',
    members: [{ ...EMPTY_MEMBER }, { ...EMPTY_MEMBER }],
  };
}

function toForm(group) {
  return {
    id: group.id,
    name: group.name,
    min_headcount: String(group.min_headcount),
    members: group.members.map((member) => ({
      location_id: String(member.location_id),
      department_id: String(member.department_id),
    })),
  };
}

function formatGroupMembers(group) {
  const hasMultipleCompanies = new Set(group.members.map((member) => member.company_name)).size > 1;
  return group.members
    .map((member) => `${hasMultipleCompanies ? `${member.company_name} · ` : ''}${member.location_name} · ${member.department_name}`)
    .join(' + ');
}

export default function SharedMinimumGroups({
  companies = [],
  locationsByCompany = {},
  departmentsByLocation = {},
  canEdit = false,
  onChanged,
  compact = false,
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const locations = useMemo(
    () => Object.values(locationsByCompany).flat(),
    [locationsByCompany],
  );
  const companyNames = useMemo(
    () => Object.fromEntries(companies.map((company) => [company.id, company.name])),
    [companies],
  );

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setGroups(await apiFetch('/api/ferie/fabbisogno-gruppi'));
    } catch (loadError) {
      setError(loadError.message || 'Impossibile caricare i gruppi condivisi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const updateMember = (index, field, value) => {
    setForm((current) => {
      const members = current.members.map((member, memberIndex) => (
        memberIndex === index
          ? { ...member, [field]: value, ...(field === 'location_id' ? { department_id: '' } : {}) }
          : member
      ));
      return { ...current, members };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form) return;
    const name = form.name.trim();
    const minHeadcount = Number(form.min_headcount);
    const members = form.members.map((member) => ({
      location_id: member.location_id,
      department_id: member.department_id,
    }));
    if (!name || !Number.isInteger(minHeadcount) || minHeadcount < 0 || minHeadcount > 10000) {
      toast.error('Inserisci un nome e un numero intero da 0 a 10.000');
      return;
    }
    if (members.some((member) => !member.location_id || !member.department_id)) {
      toast.error('Completa locale e reparto per ogni membro del gruppo');
      return;
    }
    if (new Set(members.map((member) => `${member.location_id}:${member.department_id}`)).size !== members.length) {
      toast.error('Ogni locale e reparto può comparire una sola volta');
      return;
    }
    setSaving(true);
    try {
      await apiFetch(form.id
        ? `/api/ferie/fabbisogno-gruppi/${form.id}`
        : '/api/ferie/fabbisogno-gruppi', {
        method: form.id ? 'PUT' : 'POST',
        body: JSON.stringify({ name, min_headcount: minHeadcount, members }),
      });
      await loadGroups();
      onChanged?.();
      setForm(null);
      toast.success('Salvato', { description: 'Gruppo condiviso aggiornato.' });
    } catch (saveError) {
      toast.error(saveError.message || 'Impossibile salvare il gruppo condiviso');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/ferie/fabbisogno-gruppi/${deleteTarget.id}`, { method: 'DELETE' });
      await loadGroups();
      onChanged?.();
      setDeleteTarget(null);
      toast.success('Gruppo rimosso', {
        description: 'I locali e i reparti sono rimasti intatti.',
      });
    } catch (removeError) {
      toast.error(removeError.message || 'Impossibile rimuovere il gruppo condiviso');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <section className={`overflow-hidden border border-app-border bg-app-surface ${compact ? 'rounded-xl' : 'rounded-2xl'}`} data-testid="fabbisogno-shared-groups">
        <div className={`flex flex-col border-b border-stone-100 sm:flex-row sm:items-center sm:justify-between ${compact ? 'gap-2 px-3 py-2.5' : 'gap-3 px-4 py-4 sm:items-start sm:px-5'}`}>
          <div>
            <h2 className={`font-semibold text-brand-navy ${compact ? 'text-sm' : 'font-display text-lg'}`}>Gruppi di copertura</h2>
            <p className={`${compact ? 'mt-0.5 text-xs' : 'mt-1 text-sm'} max-w-2xl text-stone-500`}>
              Un unico fabbisogno minimo può coprire più locali e viene usato come valore complessivo nella pianificazione ferie.
            </p>
          </div>
          {canEdit && (
              <Button type="button" size="sm" onClick={() => setForm(emptyForm())} disabled={Boolean(form)}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Nuovo gruppo
            </Button>
          )}
        </div>

        {loading && <div className={`${compact ? 'px-3 py-3' : 'px-4 py-6 sm:px-5'} text-sm text-stone-500`}>Caricamento gruppi condivisi…</div>}
        {!loading && error && (
          <div className="flex items-center justify-between gap-3 px-4 py-5 text-sm text-red-700 sm:px-5">
            <span>{error}</span>
            <Button type="button" variant="outline" size="sm" onClick={loadGroups}>Riprova</Button>
          </div>
        )}
        {!loading && !error && !groups.length && !form && (
          <div className={`${compact ? 'px-3 py-3' : 'px-4 py-6 sm:px-5'} text-sm text-stone-500`}>
            Nessun gruppo condiviso configurato. Le configurazioni singole restano disponibili sotto.
          </div>
        )}
        {!loading && !error && groups.map((group) => (
          <div key={group.id} className={`border-b border-stone-100 last:border-b-0 ${compact ? 'px-3 py-2.5' : 'px-4 py-4 sm:px-5'}`} data-testid={`fabbisogno-group-${group.id}`}>
            <div className={`flex gap-3 sm:items-center sm:justify-between ${compact ? 'items-center' : 'flex-col sm:flex-row sm:items-start'}`}>
              <div className="min-w-0">
                <div className={`font-semibold text-stone-800 ${compact ? 'text-sm' : ''}`}>{group.name}</div>
                <div className={`${compact ? 'mt-0.5 text-xs' : 'mt-1 text-sm'} font-semibold text-brand-navy`}>
                  Minimo complessivo: {group.min_headcount} persone
                </div>
                <div className={`${compact ? 'mt-1' : 'mt-2'} flex flex-wrap gap-x-2 text-xs text-stone-500`}>
                  {formatGroupMembers(group)}
                </div>
              </div>
              {canEdit && (
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button type="button" size="sm" variant="outline" onClick={() => setForm(toForm(group))} disabled={Boolean(form)}>
                    <Pencil className="mr-1.5 h-4 w-4" aria-hidden="true" /> Modifica
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" size="icon" variant="ghost" disabled={Boolean(form)} aria-label={`Azioni per ${group.name}`}>
                        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        className="text-red-600 focus:bg-red-50 focus:text-red-700"
                        onSelect={() => setDeleteTarget(group)}
                        data-testid={`fabbisogno-group-${group.id}-remove`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" /> Rimuovi gruppo
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
        ))}

        {form && (
          <form className="border-t border-stone-100 bg-stone-50/60 px-4 py-4 sm:px-5" onSubmit={save} data-testid="fabbisogno-group-form">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-brand-navy">{form.id ? 'Modifica gruppo condiviso' : 'Nuovo gruppo condiviso'}</h3>
                <p className="mt-1 text-xs text-stone-500">Il valore sarà applicato una sola volta alla somma dei locali selezionati.</p>
              </div>
              <Button type="button" size="icon" variant="ghost" onClick={() => setForm(null)} aria-label="Chiudi modulo gruppo">
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
              <label className="grid gap-1 text-xs font-semibold text-stone-600">
                Nome del gruppo
                <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Es. Bozzolo + Via Bionde" maxLength={160} required />
              </label>
              <label className="grid gap-1 text-xs font-semibold text-stone-600">
                Fabbisogno minimo complessivo
                <Input type="number" min="0" max="10000" step="1" value={form.min_headcount} onChange={(event) => setForm((current) => ({ ...current, min_headcount: event.target.value }))} placeholder="Numero" required />
              </label>
            </div>
            <div className="mt-4 space-y-2">
              <div className="text-xs font-semibold text-stone-600">Locali e reparti inclusi</div>
              {form.members.map((member, index) => (
                <div key={`member-${index}`} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                  <label className="grid gap-1 text-xs text-stone-500">
                    <span className="sr-only">Locale membro {index + 1}</span>
                    <select aria-label={`Locale membro ${index + 1}`} value={member.location_id} onChange={(event) => updateMember(index, 'location_id', event.target.value)} className="h-10 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-brand-navy" required>
                      <option value="">Seleziona locale</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>{companyNames[location.companyId] ? `${companyNames[location.companyId]} · ` : ''}{location.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-1 text-xs text-stone-500">
                    <span className="sr-only">Reparto membro {index + 1}</span>
                    <select aria-label={`Reparto membro ${index + 1}`} value={member.department_id} onChange={(event) => updateMember(index, 'department_id', event.target.value)} className="h-10 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm text-brand-navy" disabled={!member.location_id} required>
                      <option value="">Seleziona reparto</option>
                      {(departmentsByLocation[member.location_id] || []).map((department) => (
                        <option key={department.id} value={department.id}>{department.name}</option>
                      ))}
                    </select>
                  </label>
                  <Button type="button" size="icon" variant="ghost" className="self-end text-stone-500" onClick={() => setForm((current) => ({ ...current, members: current.members.filter((_, memberIndex) => memberIndex !== index) }))} disabled={form.members.length <= 2} aria-label={`Rimuovi membro ${index + 1}`}>
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={() => setForm((current) => ({ ...current, members: [...current.members, { ...EMPTY_MEMBER }] }))}>
                <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Aggiungi locale
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)} disabled={saving}>Annulla</Button>
              <Button type="submit" disabled={saving}>
                <Save className="mr-1.5 h-4 w-4" aria-hidden="true" /> {saving ? 'Salvataggio…' : 'Salva gruppo'}
              </Button>
            </div>
          </form>
        )}
      </section>
      <SettingsConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}
        title="Rimuovere il gruppo condiviso?"
        description={deleteTarget ? `Stai per rimuovere “${deleteTarget.name}”.` : ''}
        consequences="I locali e i reparti resteranno intatti, ma non useranno più questo fabbisogno complessivo nella pianificazione."
        confirmLabel="Rimuovi"
        cancelLabel="Annulla"
        destructive
        onConfirm={remove}
      />
    </>
  );
}